import React, { createContext, useEffect, useState } from "react";
import { api } from "../api/client";

export type User = {
  id: number;
  email: string;
  role: "user" | "editor" | "admin";
  status: "registered" | "registered_verified" | "approved" | "paid";
  created_at?: string;
};

type AuthContextValue = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("isb_jwt"));
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMe = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get<User>("/user/me");
        setUser(res.data);
      } catch {
        setToken(null);
        localStorage.removeItem("isb_jwt");
      } finally {
        setLoading(false);
      }
    };
    fetchMe();
  }, [token]);

  const login = async (email: string, password: string) => {
    const res = await api.post("/auth/login", { email, password });
    const newToken = res.data.token as string;
    const newUser = res.data.user as User;

    setToken(newToken);
    setUser(newUser);
    localStorage.setItem("isb_jwt", newToken);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("isb_jwt");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
